import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, Wallet, Bell, CheckCircle2 } from "lucide-react";
import { usePanel, isAdmin } from "./PanelLayout";
import { rm } from "@/lib/api";
import { OrderTable } from "@/components/staff/OrderTable";
import { OrderDetailDialog } from "@/components/staff/OrderDetailDialog";
import { useOrderActions } from "@/lib/useOrderActions";

const Stat = ({ icon: Icon, label, value, tone, id }) => (
  <div className="bg-white rounded-2xl border p-5 flex items-center gap-4" data-testid={id}>
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${tone}`}><Icon className="w-6 h-6" /></div>
    <div><p className="text-xs text-slate-500">{label}</p><p className="font-display text-2xl font-extrabold">{value}</p></div>
  </div>
);

export default function Dashboard() {
  const { me } = usePanel();
  return isAdmin(me) ? <AdminDashboard /> : <StaffDashboard />;
}

function StaffDashboard() {
  const { req, me, tick } = usePanel();
  const [d, setD] = useState(null);
  const [selected, setSelected] = useState(null);
  const load = useCallback(() => req("get", "/staff/orders").then(setD), [req]);
  useEffect(() => { load(); }, [load, tick]);
  const act = useOrderActions(req, load, setSelected);
  if (!d) return <p className="text-sm text-slate-500">Memuatkan...</p>;
  return (
    <div className="space-y-8 rise" data-testid="staff-dashboard">
      <div>
        <h2 className="font-display text-2xl font-bold">Selamat bertugas{me.name ? `, ${me.name}` : ""}</h2>
        <p className="text-sm text-slate-500">Pesanan yang perlu diuruskan</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Stat id="stat-new" icon={Bell} label="Baharu" value={d.counts.baharu} tone="bg-orange-100 text-orange-600" />
        <Stat id="stat-proses" icon={ShoppingBag} label="Sedang Diproses" value={d.counts.proses} tone="bg-red-100 text-red-700" />
        <Stat id="stat-done" icon={CheckCircle2} label="Selesai" value={d.counts.selesai} tone="bg-blue-100 text-blue-600" />
      </div>
      <div className="bg-white rounded-2xl border p-4 md:p-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-bold text-lg">Pesanan Terkini</h3>
          <Link to="/panel/pesanan" data-testid="dashboard-all-orders" className="text-sm text-[#B91C1C] font-semibold">Lihat semua</Link>
        </div>
        <OrderTable orders={d.orders.slice(0, 8)} onAction={act} onOpen={setSelected} />
      </div>
      <OrderDetailDialog order={selected} onClose={() => setSelected(null)} onAction={act} />
    </div>
  );
}

function AdminDashboard() {
  const { req, me, tick } = usePanel();
  const [rep, setRep] = useState(null);
  const [selected, setSelected] = useState(null);
  const load = useCallback(() => req("get", "/staff/report").then(setRep), [req]);
  useEffect(() => { load(); }, [load, tick]);
  const act = useOrderActions(req, load, setSelected);
  if (!rep) return <p className="text-sm text-slate-500">Memuatkan...</p>;

  return (
    <div className="space-y-8 rise">
      <div>
        <h2 className="font-display text-2xl font-bold">Selamat datang{me.name ? `, ${me.name}` : ""}</h2>
        <p className="text-sm text-slate-500">Ringkasan hari ini</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat id="stat-orders" icon={ShoppingBag} label="Pesanan Hari Ini" value={rep.total_orders} tone="bg-red-100 text-red-700" />
        <Stat id="stat-sales" icon={Wallet} label="Jualan Hari Ini" value={rm(rep.total_sales)} tone="bg-amber-100 text-amber-700" />
        <Stat id="stat-new" icon={Bell} label="Baharu" value={rep.by_status.baharu} tone="bg-orange-100 text-orange-600" />
        <Stat id="stat-done" icon={CheckCircle2} label="Selesai" value={rep.by_status.selesai} tone="bg-blue-100 text-blue-600" />
      </div>
      <div className="bg-white rounded-2xl border p-4 md:p-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-bold text-lg">Pesanan Terkini</h3>
          <Link to="/panel/pesanan" data-testid="dashboard-all-orders" className="text-sm text-[#B91C1C] font-semibold">Lihat semua</Link>
        </div>
        <OrderTable orders={[...rep.orders].reverse().slice(0, 8)} onAction={act} onOpen={setSelected} />
      </div>
      <OrderDetailDialog order={selected} onClose={() => setSelected(null)} onAction={act} />
    </div>
  );
}
